import { useCallback, useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { type ApolloClient } from '@apollo/client';
import { useMutation } from '@apollo/client/react';

import { useNotificationsSuggestChangesSlice } from 'src/slices';
import {
  EscrowStatusEnum,
  AgreementFormEnum,
  type INotificationChangeParams,
  EscrowPartyTypeEnum,
} from 'src/graphql/schema';
import { SuggestedChangeCreateDocument } from 'src/graphql/actions/suggestChanges/suggestedChangeCreate.mutation';

import { createRequiredContext } from '../createRequiredContext';
import { useSoftwareEscrow } from '../software-escrow';

import type { ISuggestedChangeCreateMutation } from 'src/graphql/actions/suggestChanges/suggestedChangeCreate.mutation';

const steps = ['notification', 'activation', 'asset', 'storage'] as const;

const [useNotificationsSuggestChanges, NotificationsSuggestChangesProvider] = createRequiredContext<
  Omit<ReturnType<typeof useNotificationsSuggestChangesSlice>, 'setInitialData'> & {
    sendSuggestedChanges: () => Promise<ApolloClient.MutateResult<ISuggestedChangeCreateMutation>>;
    steps: (typeof steps)[number][];
    currentStep: number;
    setCurrentStep: Dispatch<SetStateAction<number>>;
  }
>();

const NotificationsContextProvider = ({ children }: React.PropsWithChildren) => {
  const {
    softwareEscrow,
    softwareEscrow: {
      beneficiaryActivityNotification,
      beneficiarySharedInformation,
      storageRegion,
      beneficiaryDisruptionNotification,
      beneficiaryDisruptionNotificationGracePeriod,
      depositorActivityNotification,
      depositorDisruptionNotification,
      depositorDisruptionNotificationGracePeriod,
      assetVersionsLimit,
      activationNotification,
      agreement,
      status,
      currentCompanyRole,
    },
  } = useSoftwareEscrow();
  const { state, setInitialData, ...actions } = useNotificationsSuggestChangesSlice();
  const [currentStep, setCurrentStep] = useState(0);
  const steps = useMemo(
    () =>
      createWorkflowSteps({
        role: currentCompanyRole!,
        agreementForm: agreement!.agreementForm!,
        status,
        beneficiarySharedInformation,
      }),
    [currentCompanyRole, agreement, status, beneficiarySharedInformation],
  );

  useEffect(() => {
    const initialSettings = {
      beneficiaryActivityNotification,
      beneficiarySharedInformation,
      storageRegion,
      beneficiaryDisruptionNotification,
      beneficiaryDisruptionNotificationGracePeriod,
      depositorActivityNotification: depositorActivityNotification,
      depositorDisruptionNotification: depositorDisruptionNotification,
      depositorDisruptionNotificationGracePeriod,
      assetVersionsLimit,
      activationNotification,
    };

    setInitialData(initialSettings);
  }, []);
  const [createSuggestedChanges] = useMutation(SuggestedChangeCreateDocument);

  const sendSuggestedChanges = useCallback(async () => {
    const notificationSuggestedChange: INotificationChangeParams = Object.entries(state).reduce((acc, [key, value]) => {
      if (value !== softwareEscrow[key as keyof typeof softwareEscrow])
        return {
          ...acc,
          [key]: value,
        };

      return acc;
    }, {});

    return await createSuggestedChanges({
      variables: {
        escrowId: softwareEscrow.id,
        suggestedChangeParams: {
          notificationSuggestedChange,
        },
      },
    });
  }, [state]);

  const providerValue = {
    ...actions,
    state,
    sendSuggestedChanges,
    steps,
    currentStep,
    setCurrentStep,
  };

  return <NotificationsSuggestChangesProvider value={providerValue}>{children}</NotificationsSuggestChangesProvider>;
};

function createWorkflowSteps({
  role,
  agreementForm,
  status,
  beneficiarySharedInformation,
}: {
  role: EscrowPartyTypeEnum;
  agreementForm: AgreementFormEnum;
  status: EscrowStatusEnum;
  beneficiarySharedInformation: boolean;
}): (typeof steps)[number][] {
  const workflowSteps: (typeof steps)[number][] = [];

  // Step 1: Notification step
  // Depositors always see notifications, Beneficiaries only if they've shared information
  const shouldAddNotification =
    role === EscrowPartyTypeEnum.Depositor ||
    (role === EscrowPartyTypeEnum.Beneficiary && beneficiarySharedInformation);
  if (shouldAddNotification) workflowSteps.push('notification');

  // Step 2: Asset step
  // In Tripartite agreements, both parties need the asset step when beneficiary hasn't shared info yet
  if (agreementForm === AgreementFormEnum.Tripartite && !beneficiarySharedInformation) workflowSteps.push('asset');

  // Step 3: Activation step
  // Bipartite agreements require activation if not already active
  if (agreementForm === AgreementFormEnum.Bipartite && status !== EscrowStatusEnum.Active)
    workflowSteps.push('activation');

  // Step 4: Storage step
  // Required for escrows in onboarding status
  if (status === EscrowStatusEnum.Onboarding) workflowSteps.push('storage');

  return workflowSteps;
}

export { useNotificationsSuggestChanges, NotificationsContextProvider };
